import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CitationTooltipManager } from '@/components/CitationTooltip';
import {
  MatterMemoryProvider,
  useMatterMemoryContext,
} from '@/components/contexts/MatterMemoryContext';
import { useShellHeader } from '@/components/contexts/ShellHeaderContext';
import { useSidePanel } from '@/components/contexts/SidePanelContext';
import { MatterMemory } from '@/components/matter';
import { PageLoading, Error as SharedError } from '@/components/shared';
import NoCaseSelected from '@/components/shared/NoCaseSelected';
import { type HeaderTabItem, HeaderTabs } from '@/components/ui/HeaderTabs';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useFolders } from '@/hooks/useFolders';
import useMatter from '@/hooks/useMatter';
import { CLIENT_DOCUMENTS_SLUG } from '@/lib/eca-client-documents';
import { normalizeFolderPath } from '@/lib/folderPathUtils';
import { overviewDocumentSuggestions, useMatterLabels } from '@/lib/useMatterLabels';
import type { OverviewStage } from '@/src/router/search-schemas';
import type { MatterDetail } from '@/types';

const OverviewContent = ({ matterData }: { matterData: MatterDetail }) => {
  const navigate = useNavigate();
  const { slug } = useParams({ from: '/matter/$slug/overview' });
  const { stage } = useSearch({ from: '/matter/$slug/overview' });
  // Case Overview keeps the URL paramless, so we can't tell a deliberate choice
  // from a fresh landing by the URL alone. Track the clicked tab; `null` means
  // nothing chosen yet, so the stage is auto-derived from the matter.
  const [userSelectedStage, setUserSelectedStage] = useState<OverviewStage | null>(null);
  const requestedStage: OverviewStage | null =
    stage === 'early' || stage === 'overview'
      ? stage
      : userSelectedStage;
  const { folders, isLoading: isFoldersLoading } = useFolders(slug);
  const hasCanonicalClientDocumentsRoot = useMemo(
    () => folders.some((folder) => {
      const isRoot =
        folder.depth === 0 && (folder.parent_folder_id === null || folder.parent_folder_id === undefined);
      return isRoot && normalizeFolderPath(folder.path).toLowerCase() === `/${CLIENT_DOCUMENTS_SLUG}`;
    }),
    [folders],
  );
  // TODO: Remove this runtime guard once the Alembic backlog has backfilled a
  // canonical Client Documents root for every existing matter.
  const canShowEarlyCaseAssessment = !isFoldersLoading && hasCanonicalClientDocumentsRoot;
  const explicitStage =
    requestedStage === 'early' && !canShowEarlyCaseAssessment ? 'overview' : requestedStage;
  const isStageExplicit = requestedStage !== null;
  const isEarlyStage = explicitStage === 'early';
  const { isOpen, tabs, activeTab, setActiveTab, setTabs, closePanel } = useSidePanel();
  const label = useMatterLabels(matterData?.region);
  const overviewSubheader = isEarlyStage
    ? 'Automatically generated summary of the case based on initial documents.'
    : label('overview-subheader', overviewDocumentSuggestions(label, matterData?.forum));

  const {
    matterMemory,
    loadingSummary,
    isGenerating: isGeneratingCaseOverview,
    setPollingEnabled,
  } = useMatterMemoryContext();
  const hasCaseOverviewContent = Boolean(matterMemory && matterMemory.trim().length > 0);

  // `loadingSummary` is `false` on the very first render before the fetch starts.
  const hasSeenSummaryFetchRef = useRef(false);
  useEffect(() => {
    if (loadingSummary) {
      hasSeenSummaryFetchRef.current = true;
    }
  }, [loadingSummary]);
  const hasFetchedSummary = hasSeenSummaryFetchRef.current && !loadingSummary;
  const isCaseOverviewKnownEmpty =
    Boolean(matterData?.guid)
    && hasFetchedSummary
    && !hasCaseOverviewContent
    && !isGeneratingCaseOverview;

  const hasEvaluatedDefaultStageRef = useRef(false);
  useEffect(() => {
    if (hasEvaluatedDefaultStageRef.current) return;
    if (!matterData?.guid) return;
    if (isStageExplicit) {
      hasEvaluatedDefaultStageRef.current = true;
      return;
    }
    if (!hasFetchedSummary || isFoldersLoading) return;
    hasEvaluatedDefaultStageRef.current = true;
    const nextStage =
      canShowEarlyCaseAssessment && isCaseOverviewKnownEmpty ? 'early' : 'overview';
    navigate({
      to: '/matter/$slug/overview',
      params: { slug },
      search: {
        stage: nextStage === 'early' ? 'early' : undefined,
      },
      replace: true,
    });
  }, [
    matterData?.guid,
    isStageExplicit,
    hasFetchedSummary,
    isFoldersLoading,
    canShowEarlyCaseAssessment,
    isCaseOverviewKnownEmpty,
    navigate,
    slug,
  ]);

  const setOverviewStage = useCallback((nextStage: OverviewStage) => {
    if (nextStage === 'early' && !canShowEarlyCaseAssessment) return;
    setUserSelectedStage(nextStage);
    navigate({
      to: '/matter/$slug/overview',
      params: { slug },
      search: {
        stage: nextStage === 'overview' ? undefined : nextStage,
      },
      replace: true,
    });
  }, [canShowEarlyCaseAssessment, navigate, slug]);

  const handleStageChange = useCallback((value: string) => {
    if (value === 'overview' || value === 'early') {
      setOverviewStage(value);
    }
  }, [setOverviewStage]);

  useEffect(() => {
    if (stage !== 'early') return;
    if (isFoldersLoading || canShowEarlyCaseAssessment) return;
    setUserSelectedStage(null);
    navigate({
      to: '/matter/$slug/overview',
      params: { slug },
      search: { stage: undefined },
      replace: true,
    });
  }, [stage, isFoldersLoading, canShowEarlyCaseAssessment, navigate, slug]);

  // Drop the local bridge once the URL reflects it, so a later paramless
  // navigation (e.g. the Case Overview nav link) can't be overridden by a
  // stale 'early' selection.
  useEffect(() => {
    if (userSelectedStage === null) return;
    const urlStage: OverviewStage = stage ?? 'overview';
    if (urlStage === userSelectedStage) setUserSelectedStage(null);
  }, [stage, userSelectedStage]);

  // Suspend matter-memory polling while Early Case Assessment is active.
  useEffect(() => {
    if (!matterData?.guid) return;
    if (!isStageExplicit && !hasFetchedSummary) return;
    setPollingEnabled(!isEarlyStage);
  }, [
    matterData?.guid,
    isStageExplicit,
    hasFetchedSummary,
    isEarlyStage,
    setPollingEnabled,
  ]);

  const stageTabs = useMemo(() => {
    const items: HeaderTabItem[] = [
      { value: 'overview', label: 'Case Overview' },
    ];
    if (canShowEarlyCaseAssessment) {
      items.push({ value: 'early', label: 'Early Case Assessment' });
    }
    const tabValue = explicitStage === 'early' ? 'early' : 'overview';
    return (
      <HeaderTabs
        items={items}
        value={tabValue}
        onChange={handleStageChange}
        ariaLabel="Case overview views"
      />
    );
  }, [
    canShowEarlyCaseAssessment,
    explicitStage,
    handleStageChange,
  ]);

  useShellHeader({
    header: isEarlyStage
      ? 'Early Case Assessment'
      : 'Case Overview',
    subheader: overviewSubheader,
    hideBottomBorder: true,
  });

  // Handle side panel behavior when on overview page
  useEffect(() => {
    if (!isOpen) return;

    // Disable the overview tab since we're on the overview page
    const updatedTabs = tabs.map(tab =>
      tab.id === 'overview' ? { ...tab, disabled: true } : tab
    );

    // Only update if something changed
    const hasChanges = tabs.some((tab, i) => tab.disabled !== updatedTabs[i].disabled);
    if (hasChanges) {
      setTabs(updatedTabs);
    }

    // If currently viewing the overview tab
    if (activeTab === 'overview') {
      // Check if there's a document tab available
      const documentTab = updatedTabs.find(tab => tab.id === 'document' && !tab.disabled);

      if (documentTab) {
        // Switch to document tab
        setActiveTab('document');
      } else {
        // Only overview tab available, close the panel
        closePanel();
      }
    }

    // Cleanup: re-enable overview tab when leaving this page
    return () => {
      // Re-enable the overview tab when unmounting (leaving the page)
      const reenabledTabs = tabs.map(tab =>
        tab.id === 'overview' ? { ...tab, disabled: false } : tab
      );
      // Only update if there were disabled tabs
      if (tabs.some(tab => tab.id === 'overview' && tab.disabled)) {
        setTabs(reenabledTabs);
      }
    };
  }, [isOpen, tabs, activeTab, setActiveTab, setTabs, closePanel]);

  if (
    (!isStageExplicit && (!hasFetchedSummary || !hasEvaluatedDefaultStageRef.current))
    || (requestedStage === 'early' && isFoldersLoading)
  ) {
    return <PageLoading />;
  }

  return (
    <TooltipProvider>
      <CitationTooltipManager>
        <MatterMemory
          matter={matterData}
          stage={isEarlyStage ? 'early' : 'overview'}
          stageTabs={stageTabs}
        />
      </CitationTooltipManager>
    </TooltipProvider>
  );
};

const Overview = () => {
  const { isLoading, isError, matter: matterData } = useMatter();

  if (isLoading) {
    return <PageLoading />;
  }

  if (isError) {
    return <SharedError message={isError.message} />;
  }

  if (!matterData) {
    return <NoCaseSelected />;
  }

  return (
    <MatterMemoryProvider matterGuid={matterData.guid}>
      <OverviewContent key={matterData.guid} matterData={matterData} />
    </MatterMemoryProvider>
  );
};

export default Overview;
